import api, { handleResponse, handleError } from './api';

/**
 * City Portal API (Phase 3). Resolve the local square from coords, load a
 * town's square + banner, its live signal feed, and nearby towns.
 */
const squareService = {
  async resolve({ lat, lng, label } = {}) {
    try {
      return handleResponse(await api.get('/squares/resolve', { params: { lat, lng, label } }));
    } catch (e) {
      return handleError(e);
    }
  },

  /** The events page's City Portal ring: { slug, label, count } for the nearest square —
   *  people in it or posting to it right now. Read-only; never creates a square. */
  async getHere({ lat = null, lng = null } = {}) {
    const params = {};
    if (lat != null) params.lat = lat;
    if (lng != null) params.lng = lng;
    try { return handleResponse(await api.get('/squares/here', { params })); }
    catch (e) { return handleError(e); }
  },

  /** Sign-up v3's numbers for a town: `{ city, slug, peopleHere, portalsLive, eventsThisWeek }`. Anonymous. */
  async getStats(city) {
    try { return handleResponse(await api.get('/squares/stats', { params: { city } })); }
    catch (e) { return handleError(e); }
  },

  /** Logged-out "new here" landing content for a city (venue signal, cached per city). */
  async getNewHere(slug) {
    try { return handleResponse(await api.get(`/squares/${slug}/newhere`)); }
    catch (e) { return handleError(e); }
  },

  /** City Portal v2 live-data snapshot (conditions, tonight, tickets, greenlit, clubs, live-here). */
  async getTown(slug) {
    try { return handleResponse(await api.get(`/squares/${slug}/town`)); }
    catch (e) { return handleError(e); }
  },

  /** A single City Portal extras module by name (opennow/headlines/sports/newcomers/
   *  deals/alert/bulletin/water) — one box, one call, so each renders as it arrives. */
  async getTownModule(slug, name, params) {
    try { return handleResponse(await api.get(`/squares/${slug}/town/module/${name}`, params ? { params } : undefined)); }
    catch (e) { return handleError(e); }
  },

  /** Post a community-bulletin classified. Body: { category, body, expiresAt? } */
  async postBulletin(slug, data) {
    try { return handleResponse(await api.post(`/squares/${slug}/bulletin`, data)); }
    catch (e) { return handleError(e); }
  },

  /** Edit the caller's own bulletin post. Body: { category, body, expiresAt? } */
  async updateBulletin(slug, postId, data) {
    try { return handleResponse(await api.put(`/squares/${slug}/bulletin/${postId}`, data)); }
    catch (e) { return handleError(e); }
  },

  /** Remove the caller's own bulletin post. */
  async deleteBulletin(slug, postId) {
    try { return handleResponse(await api.delete(`/squares/${slug}/bulletin/${postId}`)); }
    catch (e) { return handleError(e); }
  },

  async getSquare(slug) {
    try {
      return handleResponse(await api.get(`/squares/${slug}`));
    } catch (e) {
      return handleError(e);
    }
  },

  async getFeed(slug) {
    try {
      return handleResponse(await api.get(`/squares/${slug}/feed`));
    } catch (e) {
      return handleError(e);
    }
  },

  /** Report a signal in a square (Phase 3b). Body: { eventId, category, note? } */
  async report(slug, data) {
    try {
      return handleResponse(await api.post(`/squares/${slug}/report`, data));
    } catch (e) {
      return handleError(e);
    }
  },

  async getNearby({ lat, lng, label, take = 20 } = {}) {
    try {
      return handleResponse(await api.get('/squares/nearby', { params: { lat, lng, label, take } }));
    } catch (e) {
      return handleError(e);
    }
  },

  /** Admin only: re-pick a different banner image for a town. */
  async regenerateBanner(slug) {
    try {
      return handleResponse(await api.post(`/squares/${slug}/banner/regenerate`));
    } catch (e) {
      return handleError(e);
    }
  },
};

export default squareService;
